import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'grand_theme_v4';

const DEFAULT_THEME = {
  hotelName: 'Grand Memories Resort & Spa',
  hotelTagline: '',
  hotelSymbol: '◈',
  logoUrl: '',
  faviconUrl: '',
  hotelPhone:   '(+254) 705 225 225',
  hotelEmail:   'info@grandmemories.co.ke',
  hotelAddress: 'Nakuru, Kenya',
  primaryColor: '#1B3A2D',
  dangerColor:  '#ef4444',
  mode: 'light',
  displayFont: "'Plus Jakarta Sans', -apple-system, sans-serif",
  bodyFont:    "'Plus Jakarta Sans', -apple-system, sans-serif",
  heroImage:   'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1400&q=80',
  bannerImage: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1400&q=80',
  roomImage:   'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80',
  galleryImages: [
    'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
    'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&q=80',
    'https://images.unsplash.com/photo-1568495248636-6432b97bd949?w=600&q=80',
    'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80',
  ],
  heroHeadline: 'Where the Wild Meets Luxury',
  heroSubtitle: 'A sanctuary of elegance against the breathtaking backdrop of Lake Nakuru.',
  heroTag: 'Nakuru, Kenya',
  whyTitle: 'The Finest Hospitality in Nakuru',
  whyBody: 'Nestled by Lake Nakuru, we blend world-class comfort with authentic Kenyan hospitality.',
  whyPoints: ['Award-winning restaurant & rooftop bar','Complimentary pool for all hotel guests','24/7 concierge & butler service','Fully equipped conference facilities'],
  bannerTitle: 'Your Home Away From Home',
  bannerBody: 'Whether a romantic getaway, family holiday, or corporate retreat — every stay is thoughtfully curated.',
  footerTagline: 'A sanctuary of elegance in the heart of Kenya.',
};

/* ── ServePoint-inspired palette ──────────────────────────────────────────────
   Light:  sidebar = deep forest green #1B3A2D, content bg = warm cream #F5F0E8,
           cards = pure white, gold accent #C9A84C
   Dark:   sidebar = near-black green #0F211A, content = #0c0c0e, cards = #1a1a1d
────────────────────────────────────────────────────────────────────────────── */
export function buildPalette(theme) {
  const dark = theme.mode === 'dark';
  return {
    bg:           dark ? '#0e0e10' : '#F0EBE1',       // warm cream content bg
    bgSurface:    dark ? '#161618' : '#ffffff',
    bgCard:       dark ? '#1e1e22' : '#ffffff',        // white cards
    bgHeader:     dark ? '#0F211A' : '#1B3A2D',        // dark green topbar
    bgSidebar:    dark ? '#0F211A' : '#1B3A2D',        // dark green sidebar
    bgInput:      dark ? '#252528' : '#F7F4EF',
    bgHover:      dark ? '#2a2a2e' : '#EDE8DF',
    bgActive:     dark ? '#1B3A2D' : '#C9A84C',        // gold active state
    bgModal:      dark ? 'rgba(0,0,0,0.75)' : 'rgba(10,20,15,0.55)',
    border:       dark ? '#2a2a2e' : '#E5DFD5',
    borderMid:    dark ? '#3a3a3e' : '#D4CEC4',
    borderStrong: dark ? '#52524a' : '#B0A898',
    textPrimary:  dark ? '#f0f0ee' : '#1a1a18',
    textSecond:   dark ? '#c8c8c4' : '#3d3d38',
    textMuted:    dark ? '#78786e' : '#706860',
    textFaint:    dark ? '#52524a' : '#9a9080',
    // Gold accent
    primary:      dark ? '#C9A84C' : '#C9A84C',
    primaryDark:  dark ? '#b8953e' : '#b8953e',
    accent:       dark ? '#C9A84C' : '#C9A84C',
    danger:       theme.dangerColor || '#ef4444',
    bgSuccess:    dark ? '#0a1f12' : '#f0fdf4',
    bgDanger:     dark ? '#1f0a0a' : '#fef2f2',
    bgWarning:    dark ? '#1a1000' : '#fffbeb',
    bgInfo:       dark ? '#0a1220' : '#f0f7ff',
    // Sidebar nav colors
    navBg:        dark ? '#0F211A' : '#1B3A2D',
    navText:      dark ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.70)',
    navTextActive:dark ? '#C9A84C' : '#ffffff',
    navBorder:    dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.1)',
    navAccent:    '#C9A84C',
    // Surface tiers
    bgTier1:      dark ? '#161618' : '#ffffff',
    bgTier2:      dark ? '#1e1e22' : '#F7F4EF',
    shadowCard:   dark ? '0 2px 12px rgba(0,0,0,0.5)' : '0 2px 12px rgba(0,0,0,0.06)',
    shadowHover:  dark ? '0 6px 24px rgba(0,0,0,0.6)' : '0 6px 24px rgba(0,0,0,0.1)',
  };
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Migrate: strip old hardcoded default taglines so blank is respected
        if (parsed.hotelTagline === 'Lodge & Retreat' || parsed.hotelTagline === 'Resort & Spa') {
          parsed.hotelTagline = '';
        }
        return { ...DEFAULT_THEME, ...parsed };
      }
    } catch {}
    return DEFAULT_THEME;
  });

  // #26 — Load branding from backend on every mount so all devices see the same config
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    fetch(`${API_BASE}/api/branding/`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const updates = {};
        if (data.hotel_name)    updates.hotelName    = data.hotel_name;
        if (data.hotel_tagline !== undefined) updates.hotelTagline = data.hotel_tagline;
        if (data.hotel_phone)   updates.hotelPhone   = data.hotel_phone;
        if (data.hotel_email)   updates.hotelEmail   = data.hotel_email;
        if (data.hotel_address) updates.hotelAddress = data.hotel_address;
        if (typeof data.hotel_logo_b64 === 'string' && data.hotel_logo_b64) {
          updates.logoUrl = data.hotel_logo_b64.startsWith('data:')
            ? data.hotel_logo_b64
            : `data:image/png;base64,${data.hotel_logo_b64}`;
        }
        if (Object.keys(updates).length) {
          setThemeRaw(prev => {
            const next = { ...prev, ...updates };
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
            return next;
          });
        }
      })
      .catch(() => {}); // branding fetch is non-critical
  }, []);

  const palette = buildPalette(theme);

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(palette).forEach(([k, v]) => root.style.setProperty(`--${k}`, v));
    root.style.setProperty('--primary', palette.primary);
    root.style.setProperty('--font-display', theme.displayFont);
    root.style.setProperty('--font-body', theme.bodyFont);
    document.body.style.background = palette.bg;
    document.body.style.color = palette.textPrimary;
    const faviconUrl = theme.logoUrl || theme.faviconUrl;
    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = faviconUrl;
    }
    if (theme.hotelName) document.title = theme.hotelName + (theme.hotelTagline ? ' — ' + theme.hotelTagline : '');
  }, [theme]);

  const setTheme = useCallback((updates) => {
    setThemeRaw(prev => {
      const next = { ...prev, ...updates };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleMode = useCallback(() => setTheme({ mode: theme.mode === 'dark' ? 'light' : 'dark' }), [theme.mode, setTheme]);
  const resetTheme = useCallback(() => { localStorage.removeItem(STORAGE_KEY); setThemeRaw(DEFAULT_THEME); }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleMode, resetTheme, palette }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
